import type { CaseDetailView } from '@concord/domain/contracts';
import { Button, ConfirmDialog, Dialog, Field, Icon, Menu, Notice, useToast } from '@concord/ui';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { useNavigate } from 'react-router';
import { api, errorMessage } from '../../lib/api';
import { useCaseAction } from './CaseControls';
import { ReassignCaseDialog } from './ReassignCase';

type Open = null | 'note' | 'hold' | 'guest' | 'setup' | 'owner' | 'cancel' | 'complete' | 'close';

/**
 * The page-head actions on the case screen (CON-09 to CON-15, CON-17, PST-01, PST-07).
 * Irreversible actions say what happens and whether it can be undone.
 */
export function CaseActions({ c, onSetTime }: { c: CaseDetailView; onSetTime: () => void }) {
  const navigate = useNavigate();
  const toast = useToast();
  const act = useCaseAction(c);
  const [open, setOpen] = useState<Open>(null);
  const [text, setText] = useState('');
  const close = () => {
    setOpen(null);
    setText('');
  };
  const run = (a: Parameters<typeof act.mutate>[0]) => act.mutate(a, { onSuccess: close });
  const live = ['submitted', 'on_hold', 'released', 'scheduled'].includes(c.state);

  return (
    <>
      <Button variant="sec" onClick={() => setOpen('note')}>
        Add internal note
      </Button>
      {!c.readOnly && c.state === 'submitted' && (
        <Button variant="sec" onClick={() => setOpen('hold')}>
          Hold
        </Button>
      )}
      {c.state === 'on_hold' && (
        <Button
          variant="sec"
          busy={act.isPending}
          onClick={() => act.mutate({ path: '/clear-hold', done: 'Hold cleared.' })}
        >
          Clear hold
        </Button>
      )}
      {c.consultation && (c.state === 'released' || c.state === 'scheduled') && (
        <Button variant="sec" onClick={onSetTime}>
          Reschedule
        </Button>
      )}
      {c.state === 'scheduled' && (
        <Button variant="sec" onClick={() => setOpen('complete')}>
          Mark as held
        </Button>
      )}
      {c.state === 'session_complete' && <Button onClick={() => setOpen('close')}>Close case</Button>}
      {!c.readOnly && (
        <Menu
          trigger={
            <button type="button" className="btn sec" aria-label="More case actions">
              More <Icon.ChevronDown />
            </button>
          }
          actions={[
            {
              label: 'Add physician',
              disabled: !live,
              onSelect: () => navigate(`/console/cases/${c.reference}/panel`),
            },
            { label: 'Add guest', disabled: !live, onSelect: () => setOpen('guest') },
            { label: 'Change setup', disabled: !live, onSelect: () => setOpen('setup') },
            { label: 'Reassign case', onSelect: () => setOpen('owner') },
            {
              label: c.qualityReview ? 'Clear quality review flag' : 'Flag for quality review',
              separatorBefore: true,
              onSelect: () =>
                act.mutate({
                  path: '/flags',
                  method: 'PATCH',
                  body: { qualityReview: !c.qualityReview },
                  done: 'Flag updated.',
                }),
            },
            {
              label: c.preventInvitations ? 'Allow invitations again' : 'Prevent invitations',
              onSelect: () =>
                act.mutate({
                  path: '/flags',
                  method: 'PATCH',
                  body: { preventInvitations: !c.preventInvitations },
                  done: 'Flag updated.',
                }),
            },
            {
              label: 'Copy link',
              onSelect: async () => {
                await navigator.clipboard.writeText(`${window.location.origin}/console/cases/${c.reference}`);
                toast('Link copied.');
              },
            },
            {
              label: 'Cancel case',
              danger: true,
              separatorBefore: true,
              disabled: !live,
              onSelect: () => setOpen('cancel'),
            },
          ]}
        />
      )}

      <Dialog
        open={open === 'note'}
        onOpenChange={(v) => !v && close()}
        title="Add internal note"
        description="Staff only. Participants never see it, and it cannot be edited once saved."
        footer={
          <>
            <Button variant="sec" onClick={close}>
              Cancel
            </Button>
            <Button
              disabled={!text.trim()}
              busy={act.isPending}
              onClick={() => run({ path: '/notes', body: { body: text }, done: 'Note added.' })}
            >
              Add note
            </Button>
          </>
        }
      >
        <textarea
          className="textarea"
          aria-label="Note"
          value={text}
          onChange={(e) => setText(e.target.value)}
        />
      </Dialog>

      <Dialog
        open={open === 'hold'}
        onOpenChange={(v) => !v && close()}
        title="Put the case on hold"
        description="The reason shows on the case and blocks release until you clear it."
        footer={
          <>
            <Button variant="sec" onClick={close}>
              Cancel
            </Button>
            <Button
              disabled={text.trim().length < 3}
              busy={act.isPending}
              onClick={() => run({ path: '/hold', body: { reason: text }, done: 'Case on hold.' })}
            >
              Hold
            </Button>
          </>
        }
      >
        <Field label="Reason">
          {(id) => <input id={id} className="input" value={text} onChange={(e) => setText(e.target.value)} />}
        </Field>
      </Dialog>

      {open === 'guest' && <GuestDialog c={c} onDone={close} />}
      {open === 'setup' && <SetupDialog c={c} onDone={close} />}
      {open === 'owner' && <ReassignCaseDialog c={c} onDone={close} />}

      <ConfirmDialog
        open={open === 'cancel'}
        onOpenChange={(v) => !v && close()}
        title={`Cancel ${c.reference}?`}
        consequence="Open invitations are withdrawn, any consultation is canceled, and the patient, physicians and guests are told. The case becomes read-only. This cannot be undone."
        confirmLabel="Cancel the case"
        danger
        busy={act.isPending}
        onConfirm={() =>
          text.trim().length >= 3 && run({ path: '/cancel', body: { reason: text }, done: 'Case canceled.' })
        }
      >
        <Field label="Reason (required, stays on the case)">
          {(id) => <input id={id} className="input" value={text} onChange={(e) => setText(e.target.value)} />}
        </Field>
      </ConfirmDialog>

      <ConfirmDialog
        open={open === 'complete'}
        onOpenChange={(v) => !v && close()}
        title="Mark the consultation as held?"
        consequence="The consultation is recorded as completed and the case moves to awaiting closure. The time can no longer be changed."
        confirmLabel="Mark as held"
        busy={act.isPending}
        onConfirm={() => run({ path: '/complete', done: 'Marked as held.' })}
      />
      <ConfirmDialog
        open={open === 'close'}
        onOpenChange={(v) => !v && close()}
        title={`Close ${c.reference}?`}
        consequence="The case becomes read-only. Internal notes can still be added. This cannot be undone."
        confirmLabel="Close case"
        busy={act.isPending}
        onConfirm={() => run({ path: '/close', done: 'Case closed.' })}
      />
      {act.isError && open && <Notice tone="bad">{errorMessage(act.error)}</Notice>}
    </>
  );
}

function GuestDialog({ c, onDone }: { c: CaseDetailView; onDone: () => void }) {
  const act = useCaseAction(c);
  const [g, setG] = useState({ name: '', email: '', phone: '', guestType: 'treating_physician', note: '' });
  const set = (k: keyof typeof g, v: string) => setG((x) => ({ ...x, [k]: v }));
  return (
    <Dialog
      open
      onOpenChange={(v) => !v && onDone()}
      title="Add a guest"
      description="A treating physician or a family member joining the consultation. Guests are not panel members and are never paid."
      footer={
        <>
          <Button variant="sec" onClick={onDone}>
            Cancel
          </Button>
          <Button
            disabled={g.name.trim().length < 2 || (!g.email.trim() && !g.phone.trim())}
            busy={act.isPending}
            onClick={() =>
              act.mutate(
                {
                  path: '/guests',
                  body: {
                    name: g.name,
                    email: g.email || null,
                    phone: g.phone || null,
                    guestType: g.guestType,
                    note: g.note || null,
                  },
                  done: 'Guest added.',
                },
                { onSuccess: onDone },
              )
            }
          >
            Add guest
          </Button>
        </>
      }
    >
      <Field label="Name">
        {(id) => (
          <input id={id} className="input" value={g.name} onChange={(e) => set('name', e.target.value)} />
        )}
      </Field>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '.9rem' }}>
        <Field label="Email">
          {(id) => (
            <input id={id} className="input" value={g.email} onChange={(e) => set('email', e.target.value)} />
          )}
        </Field>
        <Field label="Phone">
          {(id) => (
            <input id={id} className="input" value={g.phone} onChange={(e) => set('phone', e.target.value)} />
          )}
        </Field>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '.9rem' }}>
        <Field label="Type">
          {(id) => (
            <select
              id={id}
              className="select"
              value={g.guestType}
              onChange={(e) => set('guestType', e.target.value)}
            >
              <option value="treating_physician">Treating physician</option>
              <option value="family">Family member</option>
              <option value="other">Other</option>
            </select>
          )}
        </Field>
        <Field label="Note">
          {(id) => (
            <input
              id={id}
              className="input"
              value={g.note}
              onChange={(e) => set('note', e.target.value)}
              placeholder="e.g. treating oncologist"
            />
          )}
        </Field>
      </div>
      {act.isError && <Notice tone="bad">{errorMessage(act.error)}</Notice>}
    </Dialog>
  );
}

function SetupDialog({ c, onDone }: { c: CaseDetailView; onDone: () => void }) {
  const act = useCaseAction(c);
  const [caseTypeId, setCaseTypeId] = useState(c.caseType?.id ?? '');
  const [q, setQ] = useState('');
  const types = useQuery({
    queryKey: ['case-types', c.id, q],
    queryFn: () =>
      api<{ items: { id: string; term: string }[] }>(
        `/catalog/case-types?caseId=${c.id}&q=${encodeURIComponent(q)}`,
      ),
  });
  return (
    <Dialog
      open
      onOpenChange={(v) => !v && onDone()}
      title="Change setup"
      description="Every change is logged with what it was before. Physicians already invited keep their invitations."
      footer={
        <>
          <Button variant="sec" onClick={onDone}>
            Cancel
          </Button>
          <Button
            busy={act.isPending}
            disabled={!caseTypeId || caseTypeId === c.caseType?.id}
            onClick={() =>
              act.mutate(
                { path: '/setup', method: 'PATCH', body: { caseTypeId }, done: 'Setup changed.' },
                { onSuccess: onDone },
              )
            }
          >
            Save
          </Button>
        </>
      }
    >
      <Field label="Find a case type">
        {(id) => <input id={id} className="input" value={q} onChange={(e) => setQ(e.target.value)} />}
      </Field>
      <Field label="Case type">
        {(id) => (
          <select
            id={id}
            className="select"
            value={caseTypeId}
            onChange={(e) => setCaseTypeId(e.target.value)}
            size={6}
          >
            {types.data?.items.map((t) => (
              <option key={t.id} value={t.id}>
                {t.term}
              </option>
            ))}
          </select>
        )}
      </Field>
      {act.isError && <Notice tone="bad">{errorMessage(act.error)}</Notice>}
    </Dialog>
  );
}
